import { tryCatch } from "@bcmi-labs/art-toolkit/utils"
import { snackbar } from "@bcmi-labs/art-ui/components"
import { createFileRoute, notFound, redirect } from "@tanstack/react-router"
import * as v from "valibot"

import { provisioningSSEQuery } from "@/api/hooks/useSSEProvision"
import { $rqIotApiClient } from "@/api/iot"
import { getBoardsQueryOptions } from "@/api/query/boardsQueryOptions"
import { getXOrganizationHeaders } from "@/api/utils"
import { routeGuard } from "@/lib/routing/beforeLoad"

const provisioningSchema = v.fallback(
  v.intersect([
    v.fallback(
      v.variant("type", [
        v.object({
          type: v.literal("arduino"),
          connection: v.union([v.literal("ble"), v.literal("serial")]),
          id: v.optional(v.string())
        }),
        v.object({
          type: v.union([v.literal("3p"), v.literal("any")])
        }),
        v.object({ type: v.literal("unoq") }),
        v.object({ type: v.undefined() })
      ]),
      { type: undefined }
    ),
    v.object({ thingId: v.optional(v.string()) })
  ]),
  { type: undefined }
)

export const Route = createFileRoute("/_authlayout/_header/devices/new/")({
  validateSearch: provisioningSchema,
  beforeLoad: async ({ context }) => {
    await routeGuard(context, { permission: { flag: "iot:device::create", fallbackRoute: { to: "/devices" } } })
  },
  loader: async ({ context: { spaceStorage, queryClient }, location }) => {
    const spaceId = spaceStorage.getSpace()
    await queryClient.query({ ...getBoardsQueryOptions(), staleTime: "static" })

    const search = location.search as v.InferInput<typeof provisioningSchema>

    // Check for validity of thingId. If it is non-existent, remove the thingId from the search params.
    if (search.thingId) {
      const data = await tryCatch(() =>
        queryClient.query({
          ...$rqIotApiClient.queryOptions("get", "/iot/v2/things/{id}", {
            params: { header: getXOrganizationHeaders(spaceId), path: { id: search.thingId! } }
          }),
          staleTime: "static"
        })
      )

      if (data.error) {
        redirect({
          from: "/devices/new/",
          to: "/devices/new",
          search: prev => ({ ...prev, thingId: undefined }),
          throw: true
        })
        return
      }
    }

    // Reconcile onboarding ID if it exists.
    if (search.type === "arduino" && search.id) {
      const activeClaims = await queryClient.query({ ...provisioningSSEQuery(spaceId), staleTime: "static" })

      const currentOnboarding = activeClaims.onboardings.find(o => o.id === search.id)
      const provisionedDevice = activeClaims.events.find(o => o.id === search.id)

      // Device is already available.
      if (provisionedDevice && provisionedDevice.device_id) {
        redirect({ to: "/devices/$id", params: { id: provisionedDevice.device_id }, throw: true })
      }

      // Onboarding doesn't exist.
      if (!currentOnboarding) {
        snackbar({ message: "Onboarding ID not found", variant: "error" })
        notFound({ throw: true })
      }
    }
  },
  head: () => ({
    meta: [
      {
        title: "New Device | Arduino Cloud"
      }
    ]
  })
})
